import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Button, Card } from '@/ui/primitives'
import {
  citesteSchita, salveazaSchita, stareInitiala, stergeSchita,
  calculFiscal, nopti, nr, persoane, turistiiTuturorCamerelor, type StareWizard,
} from './stare'
import { Pas1, Pas2, Pas3, Pas4, Pas5, Pas6, useTipuriTva, type TipTva } from './pasi'

const PASI = [
  { n: 1, t: 'Serviciu' }, { n: 2, t: 'Camere si turisti' }, { n: 3, t: 'Servicii extra' },
  { n: 4, t: 'Titular si facturare' }, { n: 5, t: 'Sumar' }, { n: 6, t: 'Finalizare' },
]

type Verificare = { valid: boolean; errors?: string[]; warnings?: string[] }

/** Wizardul de rezervare. Validarea fiecarui pas o face BACKENDUL (aceleasi endpointuri
 *  ca wizardul vechi), ca sa nu existe doua seturi de reguli care se cearta. */
export function WizardRezervare() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [s, setS] = useState<StareWizard>(stareInitiala)
  const [schita, setSchita] = useState(() => citesteSchita())
  const [verif, setVerif] = useState<Verificare | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)

  const tipuri = useTipuriTva()
  const tva: TipTva[] = tipuri.data?.vat_types ?? []

  const { data: cursZi } = useQuery({
    queryKey: ['curs-bnr'],
    queryFn: () => api.get<{ eur_rate: number }>('/invoices/page', { per_page: 1 }),
    staleTime: 30 * 60_000,
  })
  const curs = cursZi?.eur_rate ?? 0

  // Schita se scrie la fiecare schimbare: serverul nu tine schite, iar munca nu are voie
  // sa dispara la un refresh sau la o intrerupere.
  useEffect(() => { if (s.destination || s.titular_name || s.rooms.length) salveazaSchita(s) }, [s])

  const set = (patch: Partial<StareWizard>) => { setVerif(null); setS((x) => ({ ...x, ...patch })) }

  const step1_data = useMemo(() => ({
    destination: s.destination, hotel: s.hotel, supplier_id: s.supplier_id,
    supplier_name: s.supplier_name, check_in: s.check_in, check_out: s.check_out,
    adults: nr(s.adults), children: nr(s.children), infants: nr(s.infants),
    num_rooms: nr(s.num_rooms), currency: s.currency,
    room_type: s.rooms[0]?.room_type, meal_type: s.rooms[0]?.meal_type,
  }), [s])

  const rooms = useMemo(() => s.rooms.map((c) => ({
    room_type: c.room_type, meal_type: c.meal_type, room_notes: c.room_notes ?? null,
    tourists: c.tourists.filter((t) => t.full_name.trim()).map((t, i) => ({
      full_name: t.full_name.trim(), birth_date: t.birth_date || null, cnp: t.cnp || null,
      id_series: t.id_series || null, id_number: t.id_number || null,
      phone: t.phone || null, email: t.email || null,
      role: t.is_titular ? 'titular_camera' : 'insotitor', is_titular: Boolean(t.is_titular),
    })),
  })), [s.rooms])

  const extra_services = useMemo(() => s.extra_services.map((x) => ({
    service_type: x.service_type, description: x.description,
    supplier_name: x.supplier_name || null, supplier_ref: x.supplier_ref || null,
    begin_date: x.begin_date || null, end_date: x.end_date || null,
    price: nr(x.sale_price), sale_price: nr(x.sale_price), cost_price: nr(x.cost_price),
    currency: x.currency, is_supplementary: true,
  })), [s.extra_services])

  const titular = useMemo(() => ({
    titular_type: s.titular_type, titular_name: s.titular_name,
    titular_cnp: s.titular_cnp || null, titular_cui: s.titular_cui || null,
    titular_phone: s.titular_phone || null, titular_email: s.titular_email || null,
    titular_address: s.titular_address || null,
  }), [s])

  const verifica = useMutation({
    mutationFn: async (pas: number): Promise<Verificare> => {
      if (pas === 1) return api.post<Verificare>('/reservations/wizard/step1/validate', {
        ...step1_data, children_ages: [],
      })
      if (pas === 2) return api.post<Verificare>('/reservations/wizard/step2/validate', { step1_data, rooms })
      if (pas === 3) return api.post<Verificare>('/reservations/wizard/step3/validate',
        { step1_data, rooms, extra_services })
      if (pas === 4) return api.post<Verificare>('/reservations/wizard/step4/validate',
        { step1_data, rooms, extra_services, titular })
      return { valid: true }
    },
    onSuccess: (r, pas) => {
      setVerif(r)
      if (r.valid) setS((x) => ({ ...x, pas: Math.min(6, pas + 1) }))
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Verificarea a esuat'),
  })

  const tipTva = tva.find((t) => t.code === s.vat_type)
  const calcul = calculFiscal(s, tipTva?.percent ?? 0, tipTva?.is_margin ?? false)

  const finalizeaza = useMutation({
    mutationFn: () => {
      if (!s.titular_name.trim()) throw new ApiError(400, 'Titularul e obligatoriu')
      if (calcul.vanzare <= 0) throw new ApiError(400, 'Pretul de vanzare trebuie completat')
      return api.post<{ success: boolean; reservation_id?: number; reservation_number?: string;
        invoices_created?: { number: string }[]; message?: string; errors?: string[] }>(
        '/reservations/wizard/step6/finalize', {
          step1_data, rooms, extra_services, titular,
          services: [{
            service_type: s.service_type,
            description: [s.service_type === 'SEJUR_CHARTER' ? 'Sejur' : s.service_type,
              s.hotel, s.destination].filter(Boolean).join(' - '),
            supplier_id: s.supplier_id, supplier_name: s.supplier_name || null,
            supplier_ref: s.supplier_ref || null,
            begin_date: s.check_in, end_date: s.check_out,
            sale_price: nr(s.sale_price), cost_price: nr(s.cost_price),
            price: nr(s.sale_price),
            currency: s.currency, sale_currency: s.currency, cost_currency: s.currency,
            destination_region: s.destination_region, vat_type: s.vat_type,
            exchange_rate: nr(s.exchange_rate) || curs,
            destination: s.destination, hotel: s.hotel,
            room_type: s.rooms[0]?.room_type, meal_type: s.rooms[0]?.meal_type,
            nights: nopti(s), is_main_service: true,
          }],
          billing_groups: s.billing_groups.map((g) => ({
            name: g.name, split_type: 'per_room', payer_type: 'tourist',
            partner_name: g.partner_name, partner_vat_no: g.partner_vat_no || null,
            invoice_amount: nr(g.invoice_amount), currency: g.currency,
            room_indices: g.room_indices, tourist_indices: [],
          })),
          pricing: {
            total_sale: calcul.vanzare, total_cost: calcul.cost,
            margin: calcul.marja, vat: calcul.tva,
            currency: s.currency, exchange_rate: nr(s.exchange_rate) || curs,
          },
          generate_bon_comanda: s.generate_bon_comanda,
          remarks: s.remarks || null,
          terms_accepted: true,
        })
    },
    onSuccess: (r) => {
      if (!r.success) { setEroare((r.errors ?? []).join(' · ') || r.message || 'Nu s-a putut crea'); return }
      stergeSchita()
      qc.invalidateQueries({ queryKey: ['rezervari'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      nav(r.reservation_id ? `/rezervari/${r.reservation_id}` : '/rezervari')
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut crea rezervarea'),
  })

  const props = { s, set, tva, curs }
  const gataDeFinal = s.titular_name.trim() !== '' && calcul.vanzare > 0

  return (
    <>
      <PageHeader
        title="Rezervare noua"
        count={`pasul ${s.pas} din 6 · ${nopti(s)} nopti · ${persoane(s)} persoane`}
        actions={
          <span className="text-[13px] text-muted tnum">
            {calcul.vanzare > 0 ? `${calcul.vanzare.toFixed(2)} ${s.currency}` : ''}
          </span>
        }
      />

      <div className="px-8 py-6 space-y-6">
        {schita && s.pas === 1 && !s.destination && (
          <Card className="p-4 flex flex-wrap items-center gap-3">
            <p className="text-[13.5px] mr-auto">
              Ai o rezervare inceputa
              {schita.salvat ? ` (${new Date(schita.salvat).toLocaleString('ro-RO')})` : ''}:
              {' '}<strong>{schita.stare.destination || 'fara destinatie'}</strong>
            </p>
            <Button onClick={() => { setS(schita.stare); setSchita(null) }}>Continua</Button>
            <Button variant="quiet" onClick={() => { stergeSchita(); setSchita(null) }}>Sterge schita</Button>
          </Card>
        )}

        {/* Pasii, cu bifa pe cei terminati */}
        <div className="flex flex-wrap gap-1.5">
          {PASI.map((p) => {
            const activ = p.n === s.pas
            return (
              <button key={p.n} onClick={() => setS((x) => ({ ...x, pas: p.n }))}
                className={'h-9 px-3.5 rounded-[var(--radius-control)] text-[13.5px] border transition-colors ' +
                  (activ ? 'bg-accent-soft border-accent text-accent-ink font-medium'
                         : p.n < s.pas ? 'border-line text-ink-soft hover:border-line-strong'
                         : 'border-line text-muted')}>
                <span className="tnum mr-1.5">{p.n}</span>{p.t}
              </button>
            )
          })}
        </div>

        {(verif?.errors?.length || verif?.warnings?.length) ? (
          <Card className={'p-4 ' + (verif.errors?.length ? 'bg-bad-soft' : 'bg-warn-soft')}>
            {verif.errors?.map((x, i) => <p key={i} className="text-[13.5px] text-bad">{x}</p>)}
            {verif.warnings?.map((x, i) => <p key={i} className="text-[13.5px] text-warn">{x}</p>)}
          </Card>
        ) : null}

        {s.pas === 1 && <Pas1 {...props} />}
        {s.pas === 2 && <Pas2 {...props} />}
        {s.pas === 3 && <Pas3 {...props} />}
        {s.pas === 4 && <Pas4 {...props} />}
        {s.pas === 5 && <Pas5 {...props} />}
        {s.pas === 6 && <Pas6 {...props} />}

        <div className="sticky bottom-0 -mx-8 px-8 py-3.5 bg-surface border-t border-line flex flex-wrap items-center gap-3">
          <Button onClick={() => setS((x) => ({ ...x, pas: Math.max(1, x.pas - 1) }))}
            disabled={s.pas === 1}>Inapoi</Button>

          {s.pas < 6 ? (
            <Button variant="primary" onClick={() => { setEroare(null); verifica.mutate(s.pas) }}
              disabled={verifica.isPending}>
              {verifica.isPending ? 'Se verifica…' : 'Mai departe'}
            </Button>
          ) : (
            <Button variant="primary" onClick={() => { setEroare(null); finalizeaza.mutate() }}
              disabled={finalizeaza.isPending || !gataDeFinal}>
              {finalizeaza.isPending ? 'Se creeaza…' : 'Creeaza rezervarea'}
            </Button>
          )}

          <Button variant="quiet" onClick={() => nav('/rezervari')}>Renunta</Button>

          {s.pas === 6 && !gataDeFinal && (
            <Badge tone="warn">completeaza titularul si pretul de vanzare</Badge>
          )}
          <span className="ml-auto text-[12.5px] text-muted">
            {turistiiTuturorCamerelor(s).filter((t) => t.full_name.trim()).length} turisti ·
            {' '}schita salvata local
          </span>
          {eroare && <span className="text-[13px] text-bad w-full">{eroare}</span>}
        </div>
      </div>
    </>
  )
}
